/**
 * useRewardsQueries — TanStack Query + mutation hooks over the Plan 02-05
 * rewards API.
 *
 * Endpoints (mounted at /api/rewards by apps/api/src/app.ts):
 *   GET  /api/rewards?for_member=<id>     Layer-2-filtered kid view
 *   GET  /api/rewards/admin               full active set (admin scope)
 *   GET  /api/rewards/claims/me           caller's own claim history
 *   GET  /api/rewards/claims/pending      admin queue
 *   POST /api/rewards                     create (admin scope) — Layer 1 inline
 *   POST /api/rewards/:id/claim           kid taps "Bytt"
 *   POST /api/rewards/claims/:id/approve  admin Godkjenn — Layer 3 may fire
 *   POST /api/rewards/claims/:id/reject   admin Avvis with reason
 *
 * D-11 / CRIT-1 reflection:
 *   - useRewardsForMember consumes the SERVER-side Layer 2 filter so the
 *     kid never sees blocked rewards. The UI doesn't need to filter again.
 *   - useClaimReward + useApproveRewardClaim surface the 422 details to the
 *     consumer; the consumer renders inline error copy (RewardClaimSheet
 *     for kid, RewardClaimCard for admin).
 *
 * Cadence:
 *   - rewards listing: staleTime 30_000 — admins changing the catalogue
 *     happens rarely; reward.* WS events from rewards.subscribers can
 *     invalidate the keys when needed (a later plan wires the WS).
 *   - claims/me + claims/pending: staleTime 30_000.
 *
 * Pattern matches Plan 02-09 useChoreActions: direct globalThis.fetch +
 * `lib/toast` for non-blocking error surface (the inline error UX is the
 * consumer's responsibility — the mutations re-throw the typed error).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Reward + RewardClaim row shapes (mirror server inferSelect) ────────────
export type RewardCategory = 'tid' | 'opplevelse' | 'mat' | 'annet';

export interface RewardRow {
  id: string;
  title: string;
  description: string | null;
  category: RewardCategory;
  point_cost: number;
  icon_key: string | null;
  active: number;
  created_by: string;
  created_at: string;
}

export type RewardClaimStatus = 'pending' | 'approved' | 'rejected';

export interface RewardClaimRow {
  id: string;
  reward_id: string;
  member_id: string;
  status: RewardClaimStatus;
  point_cost_at_claim: number;
  claimed_at: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
}

interface FetchError extends Error {
  status: number;
  body?: unknown;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    let parsed: unknown;
    try {
      parsed = await r.json();
    } catch {
      parsed = undefined;
    }
    const e = new Error(`fetch_failed:${r.status}`) as FetchError;
    e.status = r.status;
    e.body = parsed;
    throw e;
  }
  return (await r.json()) as T;
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await globalThis.fetch(url, init);
  if (!r.ok) {
    let parsed: unknown;
    try {
      parsed = await r.json();
    } catch {
      parsed = undefined;
    }
    const e = new Error(`fetch_failed:${r.status}`) as FetchError;
    e.status = r.status;
    e.body = parsed;
    throw e;
  }
  try {
    return (await r.json()) as T;
  } catch {
    return undefined as T;
  }
}

// ─── Queries ────────────────────────────────────────────────────────────────

/** Layer-2-filtered kid view. */
export function useRewardsForMember(memberId: string | null | undefined) {
  return useQuery<RewardRow[]>({
    queryKey: ['rewards', 'for-member', memberId ?? 'anonymous'],
    queryFn: () =>
      fetchJson<RewardRow[]>(`/api/rewards?for_member=${encodeURIComponent(memberId ?? '')}`),
    enabled: !!memberId,
    staleTime: 30_000,
  });
}

/** Caller's own claim history (rejected/approved/pending). */
export function useMyRewardClaims(memberId: string | null | undefined) {
  return useQuery<RewardClaimRow[]>({
    queryKey: ['reward-claims', 'me', memberId ?? 'anonymous'],
    queryFn: () => fetchJson<RewardClaimRow[]>('/api/rewards/claims/me'),
    enabled: !!memberId,
    staleTime: 30_000,
  });
}

/** Admin queue — pending reward_claims. */
export function usePendingRewardClaims(enabled = true) {
  return useQuery<RewardClaimRow[]>({
    queryKey: ['reward-claims', 'pending'],
    queryFn: () => fetchJson<RewardClaimRow[]>('/api/rewards/claims/pending'),
    enabled,
    staleTime: 30_000,
  });
}

/** Full active catalogue (admin form). */
export function useRewardsAdmin(enabled = true) {
  return useQuery<RewardRow[]>({
    queryKey: ['rewards', 'admin'],
    queryFn: () => fetchJson<RewardRow[]>('/api/rewards/admin'),
    enabled,
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Kid taps "Bytt" → POST /api/rewards/:id/claim.
 *
 * On 422 the FetchError carries `body.error` ('insufficient_points' |
 * 'reward_allergy_blocked') + `body.balance/cost/matched_keyword`. The
 * consumer (RewardClaimSheet) reads the error to render inline copy.
 *
 * D-12 boundary (mirrors useChoreActions.complete):
 *   - We optimistically prepend a pending row to `['reward-claims','me']`
 *     so RewardClaimsHistory shows it immediately on success. On error we
 *     do NOT roll back — the optimistic prepend only fires inside onSuccess
 *     so errors leave the cache untouched.
 */
export function useClaimReward(viewerId: string) {
  const qc = useQueryClient();
  return useMutation<RewardClaimRow, FetchError, string>({
    mutationFn: (rewardId) =>
      postJson<RewardClaimRow>(`/api/rewards/${rewardId}/claim`),
    onSuccess: (claim) => {
      // Optimistic prepend to the kid's history; the next refetch fixes
      // the canonical order when the server responds.
      qc.setQueryData<RewardClaimRow[]>(
        ['reward-claims', 'me', viewerId],
        (prev) => [claim, ...(prev ?? [])],
      );
      void qc.invalidateQueries({ queryKey: ['reward-claims', 'me', viewerId] });
      void qc.invalidateQueries({ queryKey: ['rewards', 'for-member', viewerId] });
      void qc.invalidateQueries({ queryKey: ['points', 'balance', viewerId] });
    },
  });
}

/** Admin Godkjenn → POST /claims/:id/approve. */
export function useApproveRewardClaim() {
  const qc = useQueryClient();
  return useMutation<RewardClaimRow, FetchError, string>({
    mutationFn: (claimId) =>
      postJson<RewardClaimRow>(`/api/rewards/claims/${claimId}/approve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reward-claims', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['reward-claims', 'me'] });
      void qc.invalidateQueries({ queryKey: ['points'] });
    },
  });
}

/** Admin Avvis → POST /claims/:id/reject with {reason}. */
export function useRejectRewardClaim() {
  const qc = useQueryClient();
  return useMutation<
    RewardClaimRow,
    FetchError,
    { claimId: string; reason: string }
  >({
    mutationFn: ({ claimId, reason }) =>
      postJson<RewardClaimRow>(`/api/rewards/claims/${claimId}/reject`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reward-claims', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['reward-claims', 'me'] });
    },
  });
}

/** Admin POST / — create reward with Layer-1 422 surface. */
export interface CreateRewardInput {
  title: string;
  description?: string;
  category: RewardCategory;
  point_cost: number;
  icon_key?: string;
}
export function useCreateReward() {
  const qc = useQueryClient();
  return useMutation<RewardRow, FetchError, CreateRewardInput>({
    mutationFn: (input) => postJson<RewardRow>('/api/rewards', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rewards', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['rewards', 'for-member'] });
    },
  });
}

// Re-export for consumers that want to discriminate on err.body shape.
export type { FetchError };
